import { StepperNumberInput } from '@src/components/common/StepperNumberInput';
import { getUnifiedValue } from '@src/utility/compositionSettings';
import {
  FONT_SIZE_DEFAULT,
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
} from '@src/utility/general';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { ControlFieldLabel } from './ControlField';

import type { ControlRenderProps, NumberControlConfiguration } from '../types';

/*
 * `observer` + no memo around `numDefault`: for auto-sized text `convertToPx`
 * resolves the size from observable renderer state, which lands after the
 * first render. Without `observer` that read is untracked; with it but memoized
 * the deps never change, so the cached value survives the re-render — the field
 * stays blank either way.
 */
const NumberControlBase = ({
  control,
  activeElement,
  modifyProperty,
  convertToVh,
  convertToPx,
  compositionMeta,
  min,
  max,
}: ControlRenderProps<NumberControlConfiguration>) => {
  const { unifiedValue } = getUnifiedValue(
    control,
    activeElement,
    compositionMeta,
  );
  // The element this control targets, for the font_size vh basis (nested
  // elements resolve vh against their containing composition, not the root).
  const targetElementId = useMemo(() => {
    let node: unknown = activeElement;
    for (const segment of control.elementPaths?.[0] ?? []) {
      const els = (node as { elements?: unknown[] } | undefined)?.elements;
      if (!Array.isArray(els)) return undefined;
      node = els[Number(segment)];
    }
    return (node as { source?: { id?: string } } | undefined)?.source?.id;
  }, [activeElement, control.elementPaths]);
  // Provide raw numeric default value (without unit) so NumericalInput stays in sync.
  const numDefault = ((): number | string | undefined => {
    // font_size is stored on the element as a viewport-relative string
    // (convertToVh writes "N vh"), which Number() can't parse — without this
    // conversion the field rendered blank despite a size being applied
    // (PLAT-11432). A missing value (auto-sized text) is passed through as
    // null so the resolver can fall back to the renderer's computed size.
    // Displayed in px, rounded to hide float noise.
    if (control.property === 'font_size' && convertToPx) {
      const px = convertToPx(
        typeof unifiedValue === 'string' || typeof unifiedValue === 'number'
          ? unifiedValue
          : null,
        targetElementId,
      );
      if (px != null) return Math.round(px * 10) / 10;
    }
    if (typeof unifiedValue === 'number') return unifiedValue;
    if (typeof unifiedValue === 'string') {
      // If the unified value includes the unit suffix, strip it for the input's internal raw value.
      if (control.unit && unifiedValue.endsWith(control.unit)) {
        return unifiedValue
          .slice(0, unifiedValue.length - control.unit.length)
          .trim();
      }
      // If it's a plain numeric string, pass it through.
      if (!isNaN(Number(unifiedValue))) return unifiedValue;
    }
    // this is a temporary fix for the error "background_border_radius cannot be null or undefined"
    if (control.property === 'background_border_radius') {
      return 0;
    }

    return undefined;
  })();

  const handleChange = (value: number | string) => {
    let valueFormatted: string | null | undefined;
    if (value === '') {
      // this is a temporary fix for the error "background_border_radius cannot be null or undefined"
      valueFormatted =
        control.property === 'background_border_radius' ? '0' : undefined;
    } else if (typeof value === 'number') {
      valueFormatted = control.unit ? `${value}${control.unit}` : String(value);
    } else {
      valueFormatted = control.unit ? `${value}${control.unit}` : value;
    }
    if (control.property === 'font_size') {
      // Same element id as the display conversion so the value round-trips
      // against one vh basis.
      valueFormatted = convertToVh?.(valueFormatted || null, targetElementId);
    }

    modifyProperty(control, valueFormatted);
  };

  const isFontSize = control.property === 'font_size';

  return (
    <>
      <ControlFieldLabel>{control.label}</ControlFieldLabel>
      <StepperNumberInput
        defaultValue={numDefault}
        unit={control.unit}
        onChange={handleChange}
        // Font size clamps to the same bounds as the canvas floating toolbar's
        // stepper; an explicit min/max from the caller still wins.
        min={min ?? (isFontSize ? FONT_SIZE_MIN : undefined)}
        max={max ?? (isFontSize ? FONT_SIZE_MAX : undefined)}
        // A blank field means "unset", not "smallest allowed".
        stepFromWhenEmpty={isFontSize ? FONT_SIZE_DEFAULT : undefined}
        decrementLabel={`Decrease ${control.label}`}
        incrementLabel={`Increase ${control.label}`}
      />
    </>
  );
};

NumberControlBase.displayName = 'NumberControl';

// Tracked read: `numDefault` resolves auto-sized text through observable
// renderer state — see the note on NumberControlBase for why it isn't memoized.
export const NumberControl = observer(NumberControlBase);
